import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type PartnerBookingBody, type PartnerRow } from '../api/client';
import { CZK, STATUS_LABEL, TYPE_LABEL } from '../lib/format';

/**
 * The partner's own pages (owner, 4 Nov 2026: "fill our gaps"). In the old extranet a partner account sees only its
 * bookings, a new booking, its price list, its settings and the prints of its bookings; here the same three screens
 * (Můj účet is the shared /ucet). Bookings go through the partner API's code on the partner's contract list, so one
 * made here and one made through the API are the same row.
 */
const kc = (n: number) => `${CZK.format(n)} Kč`;
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Prague' }).format(new Date());
const plusDays = (day: string, n: number) => new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const fmt = (wall: string) => (wall ? `${wall.slice(8, 10)}. ${wall.slice(5, 7)}. ${wall.slice(0, 4)} ${wall.slice(11, 16)}` : '');

/** Prague wall time (the yard's clock, whatever the partner's computer says) → ISO 8601 UTC for the API. */
const PRAGUE = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Prague', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
function offsetMinutes(at: Date): number {
  const p: Record<string, string> = {};
  for (const x of PRAGUE.formatToParts(at)) p[x.type] = x.value;
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour) % 24, Number(p.minute));
  return Math.round((asUtc - at.getTime()) / 60_000);
}
export function pragueIso(day: string, time: string): string {
  const guess = Date.parse(`${day}T${time}:00Z`);
  let t = guess - offsetMinutes(new Date(guess)) * 60_000;
  t = guess - offsetMinutes(new Date(t)) * 60_000;
  return new Date(t).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Nepodařilo se. Zkuste to znovu.');
const CANCELLABLE = new Set(['new', 'paid', 'pending']);
/** A cancelled, deleted or lapsed booking gets no voucher (the API refuses it too). */
const NO_VOUCHER = new Set(['canceled', 'deleted', 'noshow']);

export function Partner() {
  const [tab, setTab] = useState<'list' | 'new' | 'cenik'>('list');
  const me = useQuery({ queryKey: ['partner', 'me'], queryFn: () => api.partnerMe() });
  return (
    <div className="container">
      <div className="pagehead">
        <h1>{me.data?.name ?? 'Partner'}</h1>
        <span className="sub">partnerský účet</span>
      </div>
      {me.isError ? (
        <div className="card state" role="alert">
          {errText(me.error)}
        </div>
      ) : (
        <>
          <div className="tabs" role="tablist">
            {(
              [
                ['list', 'Moje rezervace'],
                ['new', 'Nová rezervace'],
                ['cenik', 'Ceník'],
              ] as const
            ).map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'active' : undefined} onClick={() => setTab(k)}>
                {l}
              </button>
            ))}
          </div>
          {tab === 'list' && <MyBookings />}
          {tab === 'new' && <NewBooking onDone={() => setTab('list')} />}
          {tab === 'cenik' && <MyPricelist />}
        </>
      )}
    </div>
  );
}

function MyBookings() {
  const qc = useQueryClient();
  const [from, setFrom] = useState(plusDays(today(), -7));
  const [to, setTo] = useState(plusDays(today(), 60));
  const [find, setFind] = useState('');
  const q = useQuery({ queryKey: ['partner', 'list', from, to], queryFn: () => api.partnerReservations(from, to), enabled: !!from && !!to && to >= from });
  /* Search within the partner's own bookings of the period (admin's test, 5 Nov 2026): name, SPZ, reference, number;
     letters with or without accents, any case. */
  const norm = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const shown = (rows: PartnerRow[]) => {
    const w = norm(find);
    if (!w) return rows;
    const ws = w.replace(/\s/g, '');
    return rows.filter((r) => norm(`${r.driverName} ${r.reference}`).includes(w) || norm(r.spz).replace(/\s/g, '').includes(ws) || String(r.id).includes(ws));
  };
  const cancel = useMutation({
    mutationFn: (id: number) => api.partnerCancel(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['partner', 'list'] }),
  });
  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="efgrid">
        <label className="ef">
          Příjezd od
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="ef">
          do
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="ef wide2">
          Hledat
          <input type="search" value={find} onChange={(e) => setFind(e.target.value)} placeholder="jméno klienta, SPZ, vaše reference, číslo rezervace" />
        </label>
      </div>
      {cancel.isError && (
        <div className="alert warn" role="alert">
          {errText(cancel.error)}
        </div>
      )}
      {!from || !to || to < from ? (
        <div className="state">Zvolte platné období (datum „do“ nesmí být před „od“).</div>
      ) : q.isPending ? (
        <div className="state">Načítám…</div>
      ) : q.isError ? (
        <div className="state" role="alert">
          {errText(q.error)}
        </div>
      ) : q.data.rows.length === 0 ? (
        <div className="state">V tomto období nemáte žádné rezervace.</div>
      ) : shown(q.data.rows).length === 0 ? (
        <div className="state">V tomto období nic neodpovídá hledání „{find.trim()}“.</div>
      ) : (
        <div className="tablewrap sestavawrap">
          <table className="ceniky">
            <thead>
              <tr>
                <th>Číslo</th>
                <th>Příjezd</th>
                <th>Odjezd</th>
                <th>Parkování</th>
                <th>Klient</th>
                <th>SPZ</th>
                <th>Vaše reference</th>
                <th className="num">Cena</th>
                <th>Stav</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown(q.data.rows).map((r: PartnerRow) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{fmt(r.arrival)}</td>
                  <td>{fmt(r.departure)}</td>
                  <td>{TYPE_LABEL[r.type] ?? r.type}</td>
                  <td>{r.driverName}</td>
                  <td>{r.spz}</td>
                  <td>{r.reference}</td>
                  <td className="num">{kc(r.price)}</td>
                  <td>{STATUS_LABEL[r.status] ?? r.status}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {!NO_VOUCHER.has(r.status) && (
                      <a className="btn sm" href={`/api/partner-portal/reservations/${r.id}/voucher`} target="_blank" rel="noopener">
                        Voucher
                      </a>
                    )}{' '}
                    {CANCELLABLE.has(r.status) && (
                      <button
                        type="button"
                        className="btn sm"
                        disabled={cancel.isPending}
                        onClick={() => {
                          if (window.confirm(`Zrušit rezervaci ${r.id} (${r.driverName})?`)) cancel.mutate(r.id);
                        }}
                      >
                        Zrušit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.data.rows.length >= 1000 && (
            <p className="settings-note">Zobrazeno 1 000 rezervací s nejpozdějším příjezdem. Pro starší zkraťte období.</p>
          )}
        </div>
      )}
    </div>
  );
}

function NewBooking({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const d0 = plusDays(today(), 1);
  const [f, setF] = useState({
    arrD: d0,
    arrT: '08:00',
    depD: plusDays(d0, 7),
    depT: '18:00',
    type: 'outdoor' as PartnerBookingBody['parking_type'],
    name: '',
    email: '',
    prefix: '+420',
    phone: '',
    car: '',
    vehicle: 'car' as NonNullable<PartnerBookingBody['vehicle_type']>,
    spz: '',
    flight: '',
    reference: '',
    note: '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const datesOk = !!(f.arrD && f.arrT && f.depD && f.depT) && `${f.depD} ${f.depT}` > `${f.arrD} ${f.arrT}`;
  const term = useMemo(
    () => (datesOk ? { arrival: pragueIso(f.arrD, f.arrT), departure: pragueIso(f.depD, f.depT), parking_type: f.type } : null),
    [datesOk, f.arrD, f.arrT, f.depD, f.depT, f.type],
  );
  const quote = useQuery({ queryKey: ['partner', 'quote', term], queryFn: () => api.partnerQuote(term!), enabled: !!term, retry: false });
  const body = (): PartnerBookingBody => ({
    ...term!,
    customer: { name: f.name.trim(), email: f.email.trim(), phone_prefix: f.prefix.trim(), phone_number: f.phone.trim() },
    vehicle_type: f.vehicle,
    vehicle_description: f.car.trim() || undefined,
    license_plate: f.spz.trim() || undefined,
    flight_number: f.flight.trim() || undefined,
    partner_reference: f.reference.trim() || undefined,
    note: f.note.trim() || undefined,
    currency: 'CZK',
    language: 'cs',
  });
  const m = useMutation({
    mutationFn: () => api.partnerCreate(body()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['partner', 'list'] });
      onDone();
    },
  });
  /* A field holding only spaces passes the browser's check; the guard below stops it (4–5 Nov 2026). */
  const missing = (
    [
      ['Jméno klienta', f.name],
      ['E-mail klienta', f.email],
      ['Předvolba', f.prefix],
      ['Telefon', f.phone],
    ] as const
  )
    .filter(([, v]) => !v.trim())
    .map(([l]) => l);
  /* The button is live once the price is there; an empty or wrong customer field is shown by the browser's own
     bubble at the field when it is pressed (admin's test, 5 Nov 2026: the grey line was easy to miss). */
  const ready = !!term && quote.isSuccess;
  const q = quote.data as { parking_price?: number; total_price?: number; days?: number } | undefined;
  return (
    <form
      className="card detailcard"
      style={{ marginTop: 14 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (ready && missing.length === 0 && !m.isPending) m.mutate();
      }}
    >
      <h2>Nová rezervace</h2>
      <p className="settings-note">Časy jsou pražské. Cena je podle vaší smlouvy.</p>
      <div className="efgrid">
        <label className="ef">
          Příjezd
          <input type="date" value={f.arrD} onChange={set('arrD')} required />
        </label>
        <label className="ef">
          čas
          <input type="time" value={f.arrT} onChange={set('arrT')} required />
        </label>
        <label className="ef">
          Odjezd
          <input type="date" value={f.depD} onChange={set('depD')} required />
        </label>
        <label className="ef">
          čas
          <input type="time" value={f.depT} onChange={set('depT')} required />
        </label>
        <label className="ef">
          Parkování
          <select value={f.type} onChange={set('type')}>
            <option value="outdoor">{TYPE_LABEL.cheap ?? 'Venkovní'}</option>
            <option value="garage">{TYPE_LABEL.regular ?? 'Garážové'}</option>
            <option value="vip">{TYPE_LABEL.exclusive ?? 'VIP'}</option>
          </select>
        </label>
      </div>
      <div className="pricehint" role="status" style={{ margin: '8px 0' }}>
        {!term
          ? 'Odjezd musí být po příjezdu.'
          : quote.isPending
            ? 'Počítám cenu…'
            : quote.isError
              ? errText(quote.error)
              : q
                ? `${q.days ?? ''} ${q.days === 1 ? 'den' : q.days && q.days < 5 ? 'dny' : 'dní'} · parkování ${kc(Number(q.parking_price ?? 0))} · celkem ${kc(Number(q.total_price ?? 0))}`
                : ''}
      </div>
      <div className="efgrid">
        <label className="ef">
          Jméno klienta
          <input value={f.name} onChange={set('name')} maxLength={255} required />
        </label>
        <label className="ef">
          E-mail klienta
          <input type="email" value={f.email} onChange={set('email')} maxLength={255} required />
        </label>
        <label className="ef">
          Předvolba
          <input value={f.prefix} onChange={set('prefix')} maxLength={8} required style={{ width: '6em' }} />
        </label>
        <label className="ef">
          Telefon
          <input value={f.phone} onChange={set('phone')} maxLength={32} required />
        </label>
        <label className="ef">
          Auto
          <input value={f.car} onChange={set('car')} maxLength={200} placeholder="např. Škoda Octavia, šedá" />
        </label>
        <label className="ef">
          Typ vozu
          <select value={f.vehicle} onChange={set('vehicle')}>
            <option value="car">osobní</option>
            <option value="SUV">SUV</option>
            <option value="van">dodávka</option>
          </select>
        </label>
        <label className="ef">
          SPZ
          <input value={f.spz} onChange={set('spz')} maxLength={12} />
        </label>
        <label className="ef">
          Číslo zpátečního letu
          <input value={f.flight} onChange={set('flight')} maxLength={32} placeholder="např. FR 7518" />
        </label>
        <label className="ef">
          Vaše reference
          <input value={f.reference} onChange={set('reference')} maxLength={16} />
        </label>
        <label className="ef" style={{ gridColumn: '1 / -1' }}>
          Poznámka
          <textarea value={f.note} onChange={set('note')} maxLength={2000} rows={2} />
        </label>
      </div>
      {m.isError && (
        <div className="alert warn" role="alert">
          {errText(m.error)}
        </div>
      )}
      <div className="actions">
        <button type="submit" className="btn primary" disabled={!ready || m.isPending}>
          {m.isPending ? 'Ukládám…' : 'Vytvořit rezervaci'}
        </button>
      </div>
    </form>
  );
}

function MyPricelist() {
  const q = useQuery({ queryKey: ['partner', 'pricelist'], queryFn: () => api.partnerPricelist() });
  if (q.isPending) return <div className="card state" style={{ marginTop: 14 }}>Načítám…</div>;
  if (q.isError) return <div className="card state" style={{ marginTop: 14 }} role="alert">{errText(q.error)}</div>;
  const c = q.data.columns;
  const days = Array.from({ length: 32 }, (_, i) => i);
  const cell = (v: number | null | undefined) => (v == null || v === 0 ? '—' : kc(v));
  return (
    <div className="card" style={{ marginTop: 14 }}>
      <h2>{q.data.title}</h2>
      <p className="settings-note">Cena za celý pobyt podle počtu dní; poslední řádek je každý další den nad 31.</p>
      <div className="tablewrap sestavawrap">
        <table className="mini settings cenik">
          <thead>
            <tr>
              <th>Dní</th>
              <th className="num">{TYPE_LABEL.cheap ?? 'Venkovní'}</th>
              <th className="num">{TYPE_LABEL.regular ?? 'Garážové'}</th>
              <th className="num">{TYPE_LABEL.exclusive ?? 'VIP'}</th>
            </tr>
          </thead>
          <tbody>
            {days.map((i) => (
              <tr key={i}>
                <td>{i === 31 ? 'každý další' : i + 1}</td>
                <td className="num">{cell(c.cheap[i])}</td>
                <td className="num">{cell(c.outside[i])}</td>
                <td className="num">{cell(c.inside[i])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
